// Authentication module types
// NOTE: GraphQL input types (AuthInput, VerifyInput, etc.) are auto-generated
// by 'yarn generate' and imported from '@/generated/graphql'
// This file only contains frontend-specific types not in the backend schema

// Authentication method - what credential type to use
export type AuthMethod = 'phone' | 'email' | 'username';

// Authentication step - where in the auth flow
export type AuthStep = 'input' | 'verify' | 'forgot-password' | 'reset-password' | 'no-account' | 'access-blocked';

// Information about a failed lookup, surfaced to the NoAccountFoundCard so the
// user sees exactly which identifier didn't match and what to do next.
export interface LookupFailedInfo {
  identifier: string;
  method: 'phone' | 'email';
  // The just-verified OTP, captured here so the onAccountNotFound auto-signup callback can
  // re-use it. Must live on this object because the `code` field state is cleared the moment
  // we switch to the no-account view.
  code?: string;
}

// Set when a login path answers ACCESS_BLOCKED (accountStatus on the SMS/email verify
// mutations, or `extensions.code` on username/passkey/any authenticated request):
// the credential WAS proven, but sign-in for this account has been switched off
// (terminated employee, or an admin block). Drives the AccessBlockedCard view.
export interface AccessBlockedInfo {
  identifier?: string;
  method: 'phone' | 'email' | 'username' | 'passkey' | 'session';
}

/** True when an Apollo/GraphQL error carries the backend's ACCESS_BLOCKED code. */
export const isAccessBlockedError = (err: any): boolean => {
  const codes: string[] = [];
  if (err?.graphQLErrors?.length) codes.push(...err.graphQLErrors.map((e: any) => e?.extensions?.code));
  if (err?.extensions?.code) codes.push(err.extensions.code);
  if (err?.networkError?.result?.errors?.length) codes.push(...err.networkError.result.errors.map((e: any) => e?.extensions?.code));
  return codes.includes('ACCESS_BLOCKED');
};

// Country code configuration for SMS login
export interface CountryCode {
  code: string;
  country: string;
  flag: string;
  placeholder: string;
}

// Modal props interfaces
export interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
}

export interface LoginWithSmsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
}

export interface CaptureUserDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  pendingToken?: string | null;
  onUpdateSuccess?: () => void;
  currentUserData?: {
    fName?: string;
    lName?: string;
    email?: string;
    phoneNumber?: string;
  };
}

export interface SecureMembershipButtonProps {
  size?: string;
  /** CTA label; defaults to "Get Empowered" — tenants pass their own (TLP: "Staff sign in"). */
  label?: string;
}

// User details form data (frontend form state, not GraphQL input)
export interface UserDetailsFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
}

// JWT payload interface - Updated to follow best practices
export interface JWTDecoded {
  clientId?: string;    // Best practice: include user ID in JWT for efficient lookup
  email?: string;       // Optional: for backward compatibility
  phoneNumber?: string; // Optional: for backward compatibility
  username?: string;    // Optional: for username/password auth
  tenantId?: string;    // Multi-tenant context
  exp: number;          // Expiration timestamp
  // Session assurance (2026-09-26, NIST AAL). A token without these claims is aal1.
  amr?: string[];       // factor types proven this session: "pwd" | "passkey" | "email_code" | "sms_code" | "recovery" (+ link markers)
  aal?: number;         // 1 | 2 — 2 only when amr holds two DIFFERENT factor types
  authAt?: number;      // unix seconds — first sign-in of this session
  mfaAt?: number;       // unix seconds — last second-factor proof
  iat?: number;         // unix seconds — when this token was issued
}

/** The session's assurance as the frontend reads it from the token (AuthContext `assurance`). */
export interface SessionAssurance {
  aal: 1 | 2;
  amr: string[];
  mfaAt?: number;
}

// Password strength indicator
export interface PasswordStrength {
  score: number;  // 0-4
  label: 'weak' | 'fair' | 'good' | 'strong';
  color: string;
}

// Username/Password form props
export interface UsernamePasswordFormProps {
  username: string;
  password: string;
  confirmPassword: string;
  isSignup: boolean;
  error: string;
  loading: boolean;
  usernameAvailable: boolean | null;
  checkingUsername: boolean;
  // Theme colors
  formLabelColor: string;
  formFieldBg: string;
  formFieldBorder: string;
  formFieldBorderFocus: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accentBlue: string;
  accentHover: string;
  // Handlers
  onUsernameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onToggleMode: () => void;
  onSubmit: () => void;
  onForgotPassword: () => void;
  onSwitchToPhone: () => void;
  onSwitchToEmail: () => void;
}

// Forgot password form props
export interface ForgotPasswordFormProps {
  identifier: string;
  identifierType: 'email' | 'phone';
  code: string;
  newPassword: string;
  confirmPassword: string;
  step: 'request' | 'verify' | 'reset';
  error: string;
  loading: boolean;
  // Theme colors
  formLabelColor: string;
  formFieldBg: string;
  formFieldBorder: string;
  formFieldBorderFocus: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accentBlue: string;
  accentHover: string;
  // Handlers
  onIdentifierChange: (value: string) => void;
  onIdentifierTypeChange: (type: 'email' | 'phone') => void;
  onCodeChange: (code: string) => void;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onRequestReset: () => void;
  onVerifyCode: () => void;
  onResetPassword: () => void;
  onBack: () => void;
}

// Set password form props (for existing users adding username/password)
export interface SetPasswordFormProps {
  username: string;
  password: string;
  confirmPassword: string;
  usernameAvailable: boolean | null;
  checkingUsername: boolean;
  error: string;
  loading: boolean;
  success: boolean;
  // Handlers
  onUsernameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

// Common country codes for international phone numbers
export const COUNTRY_CODES: CountryCode[] = [
  { code: "+61", country: "Australia", flag: "🇦🇺", placeholder: "412 345 678" },
  { code: "+1", country: "United States", flag: "🇺🇸", placeholder: "555 123 4567" },
  { code: "+1", country: "Canada", flag: "🇨🇦", placeholder: "555 123 4567" },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧", placeholder: "7700 900123" },
  { code: "+33", country: "France", flag: "🇫🇷", placeholder: "6 12 34 56 78" },
  { code: "+49", country: "Germany", flag: "🇩🇪", placeholder: "30 12345678" },
  { code: "+81", country: "Japan", flag: "🇯🇵", placeholder: "90 1234 5678" },
  { code: "+86", country: "China", flag: "🇨🇳", placeholder: "139 0013 8000" },
  { code: "+91", country: "India", flag: "🇮🇳", placeholder: "98765 43210" },
  { code: "+64", country: "New Zealand", flag: "🇳🇿", placeholder: "21 123 4567" },
  { code: "+65", country: "Singapore", flag: "🇸🇬", placeholder: "8123 4567" },
  { code: "+62", country: "Indonesia", flag: "🇮🇩", placeholder: "812 3456 789" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾", placeholder: "12 345 6789" },
  { code: "+66", country: "Thailand", flag: "🇹🇭", placeholder: "81 234 5678" },
  { code: "+63", country: "Philippines", flag: "🇵🇭", placeholder: "917 123 4567" },
];